import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Divider
} from "@mui/material";
import {
    Person as PersonIcon,
    Edit as EditIcon,
    Add as AddIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Business as BusinessIcon,
    Close as CloseIcon,
    Star as StarIcon,
    GroupAdd as GroupAddIcon
} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {getTrainer} from "../../services/TrainerService";
import ChangeTrainerDialog from "./ChangeTrainerDialog";
import {updateTrainer, addSecondaryTrainer, removeSecondaryTrainer} from "../../services/TraineeService";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {useMessageContext} from "../../contexts/MessageContext";
import {formatPhoneForDisplay} from "../shared/PhoneInput";
import {colors, borders, shadows} from "../ui/colors";
import {t as coachT} from "../dashboard/coach/coachTokens";
import {RTL} from "../../cache/CacheRtl";

const ClientTrainerSection = ({client, refetch, coach = false}) => {
    const [primaryTrainer, setPrimaryTrainer] = useState(null);
    const [secondaryTrainers, setSecondaryTrainers] = useState([]);
    const [, setLoading] = useState(false);
    const [editPrimaryOpen, setEditPrimaryOpen] = useState(false);
    const [addSecondaryOpen, setAddSecondaryOpen] = useState(false);

    const {notificationHandler} = useMessageContext();

    // Fetch primary trainer
    useEffect(() => {
        if (client.trainerId) {
            setLoading(true);
            getTrainer(client.trainerId)
                .then(r => setPrimaryTrainer(r.data))
                .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)))
                .finally(() => setLoading(false));
        } else {
            setPrimaryTrainer(null);
        }
    }, [client]);

    // Fetch secondary trainers
    useEffect(() => {
        if (client.secondaryTrainerIds?.length > 0) {
            Promise.all(client.secondaryTrainerIds.map(id => getTrainer(id)))
                .then(results => setSecondaryTrainers(results.map(r => r.data)))
                .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
        } else {
            setSecondaryTrainers([]);
        }
    }, [client]);

    const handleUpdatePrimary = (trainerId) => {
        updateTrainer(client.id, trainerId)
            .then(() => { refetch(); setEditPrimaryOpen(false); })
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
    };

    const handleAddSecondary = (trainerId) => {
        addSecondaryTrainer(client.id, trainerId)
            .then(() => { refetch(); setAddSecondaryOpen(false); notificationHandler.success("מאמן משני נוסף בהצלחה"); })
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
    };

    const handleRemoveSecondary = (trainerId) => {
        removeSecondaryTrainer(client.id, trainerId)
            .then(() => { refetch(); notificationHandler.success("מאמן משני הוסר בהצלחה"); })
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
    };

    const getGenderText = (gender) => {
        return gender === "MALE" ? "זכר" : "נקבה";
    };

    return (
        <RTL>
        <Paper
            dir="rtl"
            sx={{
                backgroundColor: coach ? coachT.white : colors.white,
                border: coach ? `1px solid ${coachT.border}` : borders.default,
                borderRadius: coach ? coachT.r : borders.radius.lg,
                boxShadow: coach ? coachT.sh : shadows.card,
                overflow: "hidden",
            }}
            elevation={0}
        >
            {/* Dialog for changing primary trainer */}
            <ChangeTrainerDialog
                companyId={client.companyId}
                handleSubmit={handleUpdatePrimary}
                trainer={primaryTrainer}
                close={() => setEditPrimaryOpen(false)}
                isOpen={editPrimaryOpen}
            />

            {/* Dialog for adding secondary trainer */}
            <ChangeTrainerDialog
                companyId={client.companyId}
                handleSubmit={handleAddSecondary}
                trainer={null}
                close={() => setAddSecondaryOpen(false)}
                isOpen={addSecondaryOpen}
            />

            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: coach ? "16px 22px" : "16px 20px",
                    backgroundColor: coach ? coachT.bg : colors.bg,
                    borderBottom: coach ? `1px solid ${coachT.border}` : borders.default
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: coach ? 1.5 : 1}}>
                    {coach ? (
                        <Box sx={{width: 40, height: 40, borderRadius: "11px", background: coachT.white, border: `1px solid ${coachT.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                            <PersonIcon sx={{color: coachT.t2, fontSize: 22}}/>
                        </Box>
                    ) : (
                        <PersonIcon sx={{ color: colors.textSecondary, fontSize: "20px" }} />
                    )}
                    <Box>
                        <Typography sx={{fontWeight: coach ? 800 : 600, fontSize: coach ? "17px" : "16px", color: coach ? coachT.text : colors.textPrimary, letterSpacing: coach ? "-0.2px" : 0}}>
                            מאמנים
                        </Typography>
                        {coach && <Typography sx={{fontSize: "12px", fontWeight: 500, color: coachT.t2}}>מאמן ראשי ומשניים</Typography>}
                    </Box>
                </Box>

                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                    <Tooltip title="הוסף מאמן משני">
                        <IconButton
                            onClick={() => setAddSecondaryOpen(true)}
                            sx={{
                                color: coach ? coachT.t2 : colors.success,
                                "&:hover": {
                                    backgroundColor: coach ? coachT.greenLight : colors.successBg,
                                    color: coach ? coachT.green : colors.successText
                                }
                            }}
                            size="small"
                        >
                            <GroupAddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {primaryTrainer && (
                        <Tooltip title="שינוי מאמן ראשי">
                            <IconButton
                                onClick={() => setEditPrimaryOpen(true)}
                                sx={{
                                    color: coach ? coachT.t2 : colors.textSecondary,
                                    "&:hover": {
                                        backgroundColor: coach ? coachT.greenLight : colors.bgHover,
                                        color: coach ? coachT.green : colors.textPrimary
                                    }
                                }}
                                size="small"
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{padding: "20px"}}>
                {primaryTrainer ? (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2.5}}>
                        {/* Primary Trainer */}
                        <TrainerCard
                            trainer={primaryTrainer}
                            isPrimary={true}
                            getGenderText={getGenderText}
                            coach={coach}
                        />

                        {/* Secondary Trainers */}
                        {secondaryTrainers.length > 0 && (
                            <>
                                <Divider sx={{borderColor: colors.border}} />
                                <Box>
                                    <Typography sx={{
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: colors.textMuted,
                                        marginBottom: "12px",
                                        letterSpacing: "0.3px"
                                    }}>
                                        מאמנים משניים
                                    </Typography>
                                    <Box sx={{display: "flex", flexDirection: "column", gap: 1.5}}>
                                        {secondaryTrainers.map(t => (
                                            <TrainerCard
                                                key={t.id}
                                                trainer={t}
                                                isPrimary={false}
                                                getGenderText={getGenderText}
                                                onRemove={() => handleRemoveSecondary(t.id)}
                                                coach={coach}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "80px",
                            textAlign: "center"
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 400,
                                color: colors.textSecondary,
                                marginBottom: "12px"
                            }}
                        >
                            לא הוקצה מאמן עדיין
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => setEditPrimaryOpen(true)}
                            startIcon={<AddIcon />}
                            sx={{
                                borderColor: colors.textSecondary,
                                color: colors.textSecondary,
                                fontSize: "13px",
                                padding: "6px 16px",
                                "&:hover": {
                                    borderColor: colors.textPrimary,
                                    color: colors.textPrimary,
                                    backgroundColor: colors.bg
                                }
                            }}
                            size="small"
                        >
                            בחר מאמן
                        </Button>
                    </Box>
                )}
            </Box>
        </Paper>
        </RTL>
    );
};

/* ── Trainer Card ─────────────────────────────────────── */
const TrainerCard = ({trainer, isPrimary, getGenderText, onRemove, coach = false}) => (
    <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "12px 16px",
        backgroundColor: coach ? (isPrimary ? coachT.bg : coachT.white) : (isPrimary ? colors.bg : colors.white),
        border: coach ? `1px solid ${coachT.border}` : borders.default,
        borderRadius: coach ? coachT.rs : borders.radius.md,
    }}>
        {/* Avatar */}
        <Box sx={{
            width: 40,
            height: 40,
            borderRadius: borders.radius.full,
            backgroundColor: coach ? (isPrimary ? coachT.greenLight : coachT.bg) : (isPrimary ? colors.primaryLight : colors.neutralBg),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
        }}>
            <PersonIcon sx={{color: coach ? (isPrimary ? coachT.green : coachT.t3) : (isPrimary ? colors.primary : colors.neutral), fontSize: 20}} />
        </Box>

        {/* Info */}
        <Box sx={{flex: 1, minWidth: 0}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.25}}>
                <Typography sx={{fontSize: "14px", fontWeight: coach ? 700 : 600, color: coach ? coachT.text : colors.textPrimary}}>
                    {trainer.name}
                </Typography>
                {isPrimary && (
                    <Chip
                        icon={<StarIcon sx={{fontSize: "12px !important"}} />}
                        label="ראשי"
                        size="small"
                        sx={{
                            height: "20px",
                            fontSize: "11px",
                            fontWeight: 600,
                            backgroundColor: coach ? coachT.greenLight : colors.warningBg,
                            color: coach ? coachT.greenText : colors.warningText,
                            "& .MuiChip-icon": {color: coach ? coachT.greenText : colors.warningText}
                        }}
                    />
                )}
            </Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap"}}>
                {trainer.email && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                        <EmailIcon sx={{fontSize: 14, color: colors.textMuted}} />
                        <Typography sx={{fontSize: "12px", color: colors.textSecondary}}>{trainer.email}</Typography>
                    </Box>
                )}
                {trainer.phoneNumber && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                        <PhoneIcon sx={{fontSize: 14, color: colors.textMuted}} />
                        <Typography sx={{fontSize: "12px", color: colors.textSecondary, direction: "ltr"}}>
                            {formatPhoneForDisplay(trainer.phoneNumber)}
                        </Typography>
                    </Box>
                )}
                {trainer.companyName && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                        <BusinessIcon sx={{fontSize: 14, color: colors.textMuted}} />
                        <Typography sx={{fontSize: "12px", color: colors.textSecondary}}>{trainer.companyName}</Typography>
                    </Box>
                )}
            </Box>
        </Box>

        {/* Remove button (secondary only) */}
        {!isPrimary && onRemove && (
            <Tooltip title="הסר מאמן משני">
                <IconButton
                    onClick={onRemove}
                    size="small"
                    sx={{
                        color: colors.textMuted,
                        "&:hover": {
                            backgroundColor: colors.errorBg,
                            color: colors.error
                        }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        )}
    </Box>
);

export default ClientTrainerSection;